/**
 * Print watermark helpers: defaults, sanitization, SVG generation, and iframe injection.
 * Colocated with `usePrintDocument.js` because watermarking is part of the print surface.
 *
 * Consumers:
 * - `usePrintDocument.js` — V1 print; re-exports most symbols for backward compatibility.
 * - `usePrintDocumentV2.js` — V2 decoupage print; imports from here (not from the V1 hook).
 *
 * Public exports: `DEFAULT_WATERMARK_TEXT`, `sanitizeWatermarkText`, `WATERMARK_CONFIG`,
 * `TILED_WATERMARK_CONFIG`, `generateTextWatermarkSvg`, `injectWatermark`.
 *
 * Keeps V1 and V2 watermark behavior identical without either hook importing the other.
 *
 * SECURITY NOTICE: Watermark Limitations
 *
 * The watermark generated here can be removed by users with PDF editing tools.
 * Browser-based "print to PDF" renders the watermark as a separate layer/overlay
 * rather than flattening it into the content. For tamper-proof watermarking,
 * prefer server-side PDF generation with flattened/rasterized watermarks.
 */

import _ from 'lodash';
import DOMPurify from 'dompurify';

/**
 * Default watermark text displayed when printing with watermark enabled.
 * Use \n for multi-line watermarks.
 */
export const DEFAULT_WATERMARK_TEXT = 'PRELIMINARY DRAFT -\nSUBJECT TO CHANGE';

/**
 * Sanitizes watermark text for display (e.g. after loading from DB/API).
 * Apply any normalizations here: escaped newlines, trim, etc. Pure, no side effects.
 * @param {string} [str] - Raw watermark text
 * @returns {string} Sanitized text ready for watermark rendering
 */
export function sanitizeWatermarkText(str) {
  if (str == null || typeof str !== 'string') return str;
  return str
    .replace(/\\n/g, '\n') // DB/API often returns literal \n (backslash + n)
    .trim();
}

/**
 * @typedef {Object} WatermarkConfig
 * @property {boolean} [singleWatermark=true] - When true, displays one centered watermark per page.
 *   When false, uses a tiled brick pattern with multiple watermarks.
 * @property {number} [tileWidth=816] - Tile width in pixels. Larger = fewer watermarks.
 * @property {number} [tileHeight=816] - Tile height in pixels. Larger = fewer watermarks.
 * @property {number} [fontSize=72] - Font size for text watermarks in pixels.
 * @property {number} [columns=1] - Number of columns in the pattern. More = wider coverage.
 * @property {number} [opacity=0.35] - Watermark opacity (0-1). Lower = more subtle.
 * @property {string} [fillColor] - Fill color for text watermarks (CSS color string).
 * @property {number} [rotation=-45] - Rotation angle in degrees. -45 is standard diagonal.
 */

/**
 * Watermark layout configuration
 *
 * These values control both text watermarks (watermarkText) and custom SVG watermarks (watermarkSvg).
 * Custom SVGs are scaled to fit within TILE_WIDTH x TILE_HEIGHT dimensions.
 *
 * Default configuration creates a single large centered watermark per page, suitable for
 * draft documents. The large tile size ensures one watermark is prominently displayed
 * in the center of each page.
 *
 * To customize: pass `watermarkConfig` into `usePrintDocument` or `usePrintDocumentV2` options.
 */
export const WATERMARK_CONFIG = {
  /**
   * When true, displays a single centered watermark per page.
   * When false, uses a tiled brick pattern with multiple watermarks.
   */
  SINGLE_WATERMARK: true,

  /**
   * Tile dimensions in pixels.
   * 816x816px creates approximately one watermark per page on US Letter paper (8.5" x 11" at 96 DPI).
   * This produces a single centered watermark that's clearly visible without being repetitive.
   */
  TILE_WIDTH: 816,
  TILE_HEIGHT: 816,

  /**
   * Font size in pixels.
   * 72px provides large, clearly readable text for single-watermark-per-page layout.
   * With the -45° rotation, the diagonal span is ~100px, fitting well within the large tile.
   */
  FONT_SIZE: 72,

  /**
   * Number of columns in the SVG pattern.
   * 1 column creates a single watermark horizontally. Combined with large tile dimensions,
   * this produces one centered watermark per page.
   */
  COLUMNS: 1,

  /**
   * Opacity for watermark elements (0.0 to 1.0).
   * 15% opacity is subtle enough to not obscure text but visible enough
   * to clearly indicate draft status. Slightly lower than tiled patterns
   * since there's only one watermark to convey the message.
   */
  OPACITY: 0.35,

  /**
   * Watermark fill color with opacity.
   * Gray (160, 160, 160) is neutral and works on both light and dark content.
   * Uses OPACITY value for alpha channel.
   */
  FILL_COLOR: 'rgba(160, 160, 160, 0.15)',

  /**
   * Rotation angle in degrees.
   * -45° is the standard watermark angle, creating a diagonal that's easy to read
   * and visually distinct from horizontal document text. Negative rotates counter-clockwise.
   */
  ROTATION: -45,
};

/**
 * Constants for dynamic watermark font scaling (pure function tuning).
 * Used only by computeWatermarkFontSize so the text fills the content area.
 */
const WATERMARK_FONT_SCALE = {
  /** Approximate character width as fraction of font size (Arial). */
  CHAR_WIDTH_EM: 0.55,
  /** Line height as multiple of font size. */
  LINE_HEIGHT_EM: 1.2,
  /** sin(45°) ≈ 0.707 for rotated bbox side from (width + height). */
  ROTATED_SIDE_FACTOR: 0.707,
  /** Target fill: text bbox side ≤ fillRatio * content dimension. */
  FILL_RATIO: 0.9,
  MIN_FONT_SIZE: 24,
  MAX_FONT_SIZE: 500,
  /** Max line length to treat as "short" text; short text fills max(width, height) to use full content area. */
  SHORT_TEXT_MAX_LINE_LENGTH: 10,
};

/**
 * Pure: returns line count and max line length for watermark text.
 * @param {string} text - Watermark text (may contain \n)
 * @returns {{ lineCount: number, maxLineLength: number }}
 */
function getWatermarkTextMetrics(text) {
  const lines = (text || '').split('\n').filter((line) => line.length > 0);
  const lineCount = Math.max(1, lines.length);
  const maxLineLength =
    lines.length === 0 ? 1 : Math.max(...lines.map((line) => line.length), 1);
  return {lineCount, maxLineLength};
}

/**
 * Pure: returns true when text is a single short line (e.g. "DRAFT", "CONFIDENTIAL").
 * Short text is sized to fill max(contentWidth, contentHeight) so we use the full content area.
 * @param {string} text - Watermark text (may contain \n)
 * @returns {boolean}
 */
function isShortWatermarkText(text) {
  const {lineCount, maxLineLength} = getWatermarkTextMetrics(text);
  return (
    lineCount === 1 &&
    maxLineLength <= WATERMARK_FONT_SCALE.SHORT_TEXT_MAX_LINE_LENGTH
  );
}

/**
 * Pure: computes font size (px) so watermark text fills the content area when rotated -45°.
 * No DOM, no side effects. Easy to unit test.
 *
 * @param {string} text - Watermark text (may contain \n)
 * @param {number} contentWidth - Content area width (px)
 * @param {number} contentHeight - Content area height (px)
 * @param {Object} [options] - Optional overrides
 * @param {number} [options.fillRatio] - Override WATERMARK_FONT_SCALE.FILL_RATIO
 * @param {number} [options.minFontSize] - Override min clamp
 * @param {number} [options.maxFontSize] - Override max clamp
 * @param {number} [options.configFontSizeCap] - User config fontSize as max cap (optional)
 * @param {boolean} [options.fillLargerDimension] - When true, prefer max(w,h) but still capped so rotated bbox fits (no clipping).
 * @returns {number} Font size in pixels
 */
function computeWatermarkFontSize(
  text,
  contentWidth,
  contentHeight,
  options = {}
) {
  const {
    FILL_RATIO,
    MIN_FONT_SIZE,
    MAX_FONT_SIZE,
    CHAR_WIDTH_EM,
    LINE_HEIGHT_EM,
    ROTATED_SIDE_FACTOR,
  } = WATERMARK_FONT_SCALE;

  const fillRatio = options.fillRatio ?? FILL_RATIO;
  const minFontSize = options.minFontSize ?? MIN_FONT_SIZE;
  const maxFontSize = options.maxFontSize ?? MAX_FONT_SIZE;
  const {configFontSizeCap, fillLargerDimension} = options;

  if (contentWidth <= 0 || contentHeight <= 0) {
    return minFontSize;
  }

  const contentMin = Math.min(contentWidth, contentHeight);
  const contentMax = Math.max(contentWidth, contentHeight);
  // Rotated bbox is a square of side ~0.707*(W+H); it must fit in the tile so we cannot exceed contentMin.
  const maxTargetSide = fillRatio * contentMin;
  const preferredTargetSide =
    fillRatio * (fillLargerDimension ? contentMax : contentMin);
  const targetSide = Math.min(preferredTargetSide, maxTargetSide);

  const {lineCount, maxLineLength} = getWatermarkTextMetrics(text);
  const coeff = CHAR_WIDTH_EM * maxLineLength + LINE_HEIGHT_EM * lineCount;
  const rawSize = targetSide / ROTATED_SIDE_FACTOR / coeff;

  let fontSize = Math.round(
    Math.max(minFontSize, Math.min(maxFontSize, rawSize))
  );

  const hasValidCap =
    typeof configFontSizeCap === 'number' && !Number.isNaN(configFontSizeCap);
  if (hasValidCap) {
    fontSize = Math.min(fontSize, configFontSizeCap);
  }

  return fontSize;
}

/**
 * Pure: returns true if dimensions object has valid positive width and height.
 * @param {{ contentWidth?: number, contentHeight?: number } | null} dimensions
 * @returns {boolean}
 */
function areContentDimensionsValid(dimensions) {
  if (dimensions == null) return false;
  const {contentWidth, contentHeight} = dimensions;
  return (
    typeof contentWidth === 'number' &&
    typeof contentHeight === 'number' &&
    contentWidth > 0 &&
    contentHeight > 0
  );
}

/**
 * Preset configuration for tiled watermark pattern.
 * Creates a repeating brick pattern that's harder to remove by cropping.
 * Use this when you want multiple watermarks across each page.
 *
 * @example
 * import usePrintDocument, { TILED_WATERMARK_CONFIG } from './usePrintDocument';
 *
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkText: 'DRAFT',
 *   watermarkConfig: TILED_WATERMARK_CONFIG,
 * });
 */
export const TILED_WATERMARK_CONFIG = {
  singleWatermark: false,
  tileWidth: 200,
  tileHeight: 200,
  fontSize: 46,
  columns: 6,
  opacity: 0.35,
  fillColor: 'rgba(160, 160, 160, 0.18)',
  rotation: -45,
};

/**
 * Generates a text-based SVG watermark pattern.
 * Pure: same inputs → same outputs; no DOM. When contentDimensions is present
 * and single-watermark mode, font size and tile size are derived from content.
 *
 * @param {string} text - The text to display as watermark
 * @param {WatermarkConfig} [customConfig] - Optional custom config to override defaults
 * @param {{ contentWidth: number, contentHeight: number }} [contentDimensions] - Optional content area size for dynamic scaling
 * @returns {Object} Object containing svgPattern, svgWidth, and svgHeight
 */
export const generateTextWatermarkSvg = (
  text,
  customConfig = {},
  contentDimensions = null
) => {
  const {
    SINGLE_WATERMARK,
    TILE_WIDTH: defaultTileWidth,
    TILE_HEIGHT: defaultTileHeight,
    FONT_SIZE: defaultFontSize,
    COLUMNS: defaultColumns,
    OPACITY: defaultOpacity,
    ROTATION: defaultRotation,
  } = WATERMARK_CONFIG;

  const singleWatermark = customConfig.singleWatermark ?? SINGLE_WATERMARK;
  const hasContentDimensions = areContentDimensionsValid(contentDimensions);

  // Default watermark text must stay at fixed 72px / 816×816 tile (no change from main)
  const normalizedText = (text || '').trim();
  const isDefaultWatermarkText =
    normalizedText === (DEFAULT_WATERMARK_TEXT || '').trim();

  const useDynamicScaling =
    singleWatermark && hasContentDimensions && !isDefaultWatermarkText;

  const {contentWidth, contentHeight} = contentDimensions ?? {};
  const tileWidth = useDynamicScaling
    ? contentWidth
    : (customConfig.tileWidth ?? defaultTileWidth);
  const tileHeight = useDynamicScaling
    ? contentHeight
    : (customConfig.tileHeight ?? defaultTileHeight);

  const fontSize = useDynamicScaling
    ? computeWatermarkFontSize(text, contentWidth, contentHeight, {
        configFontSizeCap: customConfig.fontSize ?? undefined,
        fillLargerDimension: isShortWatermarkText(text),
      })
    : (customConfig.fontSize ?? defaultFontSize);

  const columns = customConfig.columns ?? defaultColumns;
  const opacity = customConfig.opacity ?? defaultOpacity;
  const rotation = customConfig.rotation ?? defaultRotation;
  const fillColor = customConfig.fillColor ?? `rgba(160, 160, 160, ${opacity})`;

  // SVG dimensions: columns tiles wide
  // Single watermark mode: one centered watermark per tile (single row)
  // Tiled mode: brick pattern with 2 offset rows for harder cropping removal
  const svgWidth = tileWidth * columns;
  const svgHeight = singleWatermark ? tileHeight : tileHeight * 2;

  // Split text by newlines to support multi-line watermarks
  const lines = text.split('\n');

  // Escape each line to prevent XSS and SVG parsing issues.
  // This converts HTML special chars to entities (e.g., '<' → '&lt;', '&' → '&amp;').
  //
  // INTENTIONAL DOUBLE ENCODING: The escaped text is later encoded again via
  // encodeURIComponent() when creating the data URI. This results in entities
  // being percent-encoded (e.g., '&amp;' → '%26amp%3B'). This is correct and safe:
  // - First pass (_.escape): Prevents SVG parsing issues and XSS
  // - Second pass (encodeURIComponent): Required for valid data URI syntax
  // The browser decodes the percent-encoding, then the SVG parser interprets
  // the HTML entities, producing the original text visually.
  const safeLines = lines.map((line) => _.escape(line));

  // Build text content - use tspans for multi-line, plain text for single line
  // Line height is 1.2em (standard for readable text)
  const buildTextContent = (x) => {
    if (safeLines.length === 1) {
      return safeLines[0];
    }
    // For multi-line: first tspan has no dy offset, subsequent lines offset by 1.2em
    return safeLines
      .map((line, idx) => {
        const dy = idx === 0 ? '0' : '1.2em';
        return `<tspan x="${x}" dy="${dy}">${line}</tspan>`;
      })
      .join('');
  };

  // Calculate vertical offset for multi-line text to keep it centered
  // Each additional line shifts the starting y position up by half the total text height
  const lineHeightEm = 1.2;
  const totalTextHeight = fontSize * lineHeightEm * (safeLines.length - 1);
  const yOffset = totalTextHeight / 2;

  // Build row 1: watermarks centered within each tile
  const row1 = Array.from({length: columns}, (_, i) => {
    const x = tileWidth * (i + 0.5);
    const y = tileHeight * 0.5 - yOffset;
    return `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="normal" fill="${fillColor}" text-anchor="middle" dominant-baseline="middle" transform="rotate(${rotation}, ${x}, ${tileHeight * 0.5})">${buildTextContent(x)}</text>`;
  }).join('\n      ');

  // Build row 2: watermarks at tile edges for brick pattern offset (only in tiled mode)
  // This creates the staggered pattern that makes cropping removal harder
  const row2 = singleWatermark
    ? ''
    : Array.from({length: columns}, (_, i) => {
        const x = tileWidth * i;
        const y = tileHeight * 1.5 - yOffset;
        return `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="normal" fill="${fillColor}" text-anchor="middle" dominant-baseline="middle" transform="rotate(${rotation}, ${x}, ${tileHeight * 1.5})">${buildTextContent(x)}</text>`;
      }).join('\n      ');

  const svgPattern = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
      ${row1}
      ${singleWatermark ? '' : row2}
    </svg>
  `;

  return {svgPattern, svgWidth, svgHeight};
};

/**
 * Injects watermark styles and elements into the print iframe
 * Creates a repeating tiled pattern using SVG background that tiles properly per page
 *
 * @param {Document} iframeDoc - The iframe document to inject watermark into
 * @param {Object} options - Watermark options
 * @param {string} [options.text] - Text to display as watermark (used if svg not provided)
 * @param {string} [options.svg] - Custom SVG string to use as watermark pattern (takes precedence).
 *   The SVG will be scaled to fit within the tile dimensions.
 * @param {WatermarkConfig} [options.config] - Custom config to override WATERMARK_CONFIG defaults.
 * @param {number} [options.contentWidth] - Content area width (px) for dynamic text scaling; skip if missing/invalid.
 * @param {number} [options.contentHeight] - Content area height (px) for dynamic text scaling; skip if missing/invalid.
 */
export const injectWatermark = (iframeDoc, options) => {
  const {
    text: watermarkTextOption,
    svg: customSvg,
    config: customConfig = {},
  } = options ?? {};

  // Remove any existing watermark elements to prevent duplicates.
  const existingStyle = iframeDoc.getElementById('print-watermark-style');
  const existingOverlay = iframeDoc.getElementById('print-watermark');
  if (existingStyle) existingStyle.remove();
  if (existingOverlay) existingOverlay.remove();

  const {
    TILE_WIDTH: defaultTileWidth,
    TILE_HEIGHT: defaultTileHeight,
    COLUMNS: defaultColumns,
    OPACITY: defaultOpacity,
  } = WATERMARK_CONFIG;

  const tileWidth = customConfig.tileWidth ?? defaultTileWidth;
  const tileHeight = customConfig.tileHeight ?? defaultTileHeight;
  const columns = customConfig.columns ?? defaultColumns;
  const opacity = customConfig.opacity ?? defaultOpacity;

  let svgPattern, svgWidth, svgHeight;

  if (customSvg) {
    // Custom SVG provided (e.g., logo watermark)
    // The SVG content is extracted and scaled to fit within tile dimensions.
    // Scale factor is calculated from the SVG's viewBox for accurate sizing.
    // Opacity is automatically applied to ensure the SVG appears as a watermark.
    svgWidth = tileWidth * columns;
    svgHeight = tileHeight * 2;

    // Sanitize SVG to prevent injection attacks (removes scripts, event handlers, etc.)
    // Defense in depth: FORBID_TAGS and FORBID_ATTR are technically redundant since
    // DOMPurify's SVG profile already forbids scripts and event handlers. We explicitly
    // list them anyway to:
    // 1. Make security requirements visible in code (self-documenting)
    // 2. Guard against future DOMPurify profile changes
    // 3. Ensure these specific vectors are blocked even if profile behavior changes
    const sanitizedSvg = DOMPurify.sanitize(customSvg, {
      USE_PROFILES: {svg: true, svgFilters: true},
      FORBID_TAGS: ['script', 'style'],
      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover'],
    });

    // Extract viewBox dimensions to calculate proper scale factor.
    // viewBox format: "minX minY width height" - we need the width value.
    //
    // DEFAULT_VIEWBOX_SIZE = 200 matches WATERMARK_CONFIG.TILE_WIDTH, so SVGs without
    // a viewBox are assumed to be designed for a 200x200 canvas, resulting in scale=1.
    // This preserves backward compatibility with SVGs created before dynamic scaling.
    const DEFAULT_VIEWBOX_SIZE = 200;

    // Try quoted viewBox first (standard SVG: viewBox="0 0 100 100" or viewBox='0 0 100 100')
    // Then try unquoted viewBox as fallback (non-standard but defensive: viewBox=0 0 100 100)
    const quotedMatch = sanitizedSvg.match(/viewBox=["']([^"']+)["']/i);
    const unquotedMatch = sanitizedSvg.match(
      /viewBox=([^\s>"']+(?:\s+[^\s>"']+){3})/i
    );
    const viewBoxValue = quotedMatch?.[1] || unquotedMatch?.[1];

    let svgViewBoxWidth = DEFAULT_VIEWBOX_SIZE;
    if (viewBoxValue) {
      const viewBoxParts = viewBoxValue.trim().split(/\s+/);
      if (viewBoxParts.length >= 3) {
        const parsedWidth = parseFloat(viewBoxParts[2]);
        if (!isNaN(parsedWidth) && parsedWidth > 0) {
          svgViewBoxWidth = parsedWidth;
        }
      }
    }
    const scaleFactor = tileWidth / svgViewBoxWidth;

    // Extract SVG inner content (remove outer <svg> tags)
    const svgContent = sanitizedSvg.replace(/<\/?svg[^>]*>/g, '');

    // Create a pattern that tiles the custom SVG using the same brick layout as text
    // Each SVG instance is wrapped in a group with opacity to ensure watermark appearance
    const row1 = Array.from(
      {length: columns},
      (_, i) =>
        `<g transform="translate(${tileWidth * i}, ${tileHeight * 0.5 - tileHeight / 2})" opacity="${opacity}">
          <g transform="scale(${scaleFactor})">
            ${svgContent}
          </g>
        </g>`
    ).join('\n');

    const row2 = Array.from(
      {length: columns},
      (_, i) =>
        `<g transform="translate(${tileWidth * (i + 0.5)}, ${tileHeight * 1.5 - tileHeight / 2})" opacity="${opacity}">
          <g transform="scale(${scaleFactor})">
            ${svgContent}
          </g>
        </g>`
    ).join('\n');

    svgPattern = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
        ${row1}
        ${row2}
      </svg>
    `;
  } else {
    // Generate text-based watermark SVG with custom config if provided
    const {contentWidth, contentHeight} = options ?? {};
    const contentDimensions = areContentDimensionsValid({
      contentWidth,
      contentHeight,
    })
      ? {contentWidth, contentHeight}
      : null;

    const generated = generateTextWatermarkSvg(
      watermarkTextOption || DEFAULT_WATERMARK_TEXT,
      customConfig,
      contentDimensions
    );
    svgPattern = generated.svgPattern;
    svgWidth = generated.svgWidth;
    svgHeight = generated.svgHeight;
  }

  // Determine if single watermark mode (centered, no repeat)
  const singleWatermark =
    customConfig.singleWatermark ?? WATERMARK_CONFIG.SINGLE_WATERMARK;

  // Encode SVG for use in CSS
  const encodedSvg = encodeURIComponent(svgPattern.trim());
  const backgroundUrl = `url("data:image/svg+xml,${encodedSvg}")`;

  // CSS differs based on mode:
  // - Single watermark: no-repeat, centered on each page
  // - Tiled: repeating brick pattern
  const backgroundRepeat = singleWatermark ? 'no-repeat' : 'repeat';
  const backgroundPosition = singleWatermark ? 'center center' : 'top left';
  const backgroundSize = singleWatermark
    ? 'auto' // Use natural SVG size, centered
    : `${svgWidth}px ${svgHeight}px`;

  // Create watermark style element
  const watermarkStyle = iframeDoc.createElement('style');
  watermarkStyle.id = 'print-watermark-style';
  watermarkStyle.textContent = `
    .print-watermark-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      background-image: ${backgroundUrl};
      background-repeat: ${backgroundRepeat};
      background-position: ${backgroundPosition};
      background-size: ${backgroundSize};
    }
    
    @media print {
      .print-watermark-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;
  iframeDoc.head.appendChild(watermarkStyle);

  // Create watermark overlay element
  const watermarkOverlay = iframeDoc.createElement('div');
  watermarkOverlay.id = 'print-watermark';
  watermarkOverlay.className = 'print-watermark-overlay';
  iframeDoc.body.appendChild(watermarkOverlay);
};
